import { forwardRef, type ButtonHTMLAttributes } from 'react';
import type { CarbonIconType } from '@carbon/icons-react';
import type { Placement } from '@floating-ui/react';
import { cx } from '@/shared/lib/cx';
import { buttonClassName, type ButtonSize, type ButtonVariant } from './Button';
import s from './Button.module.css';
import { Tooltip } from './Tooltip';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
  /** Доступное имя кнопки; оно же показывается во всплывающей подсказке. Обязательно. */
  label: string;
  icon: CarbonIconType;
  variant?: Extract<ButtonVariant, 'ghost' | 'secondary' | 'primary' | 'tertiary' | 'danger-ghost'>;
  size?: ButtonSize;
  /** Счётчик на кнопке (например, непрочитанные уведомления). 0 — не показывается. */
  badge?: number;
  /** Текст счётчика для экранных дикторов (добавляется к имени кнопки). */
  badgeLabel?: string;
  /**
   * Вид счётчика: `count` — число, не больше «9+»; `dot` — точка без числа (уведомления, DESIGN.md 1.5).
   * Точное число в обоих случаях — в доступном имени кнопки.
   */
  badgeStyle?: 'count' | 'dot';
  tooltipPlacement?: Placement;
  /** Не показывать всплывающую подсказку (например, внутри меню с видимой подписью). */
  hideTooltip?: boolean;
  iconSize?: 16 | 20 | 24;
}

/** Кнопка-иконка с обязательной подписью (WCAG 4.1.2) и подсказкой при наведении. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon: Icon, variant = 'ghost', size = 'md', badge = 0, badgeLabel, badgeStyle = 'count', tooltipPlacement = 'bottom', hideTooltip = false, iconSize = 20, className, type, ...rest },
  ref,
) {
  const accessibleName = badge > 0 && badgeLabel ? `${label}. ${badgeLabel}` : label;
  const button = (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={accessibleName}
      className={cx(buttonClassName({ variant, size }), s.iconOnly, className)}
      {...rest}
    >
      <Icon size={iconSize} aria-hidden="true" />
      {badge > 0 && badgeStyle === 'dot' ? <span className={s.badgeDot} aria-hidden="true" /> : null}
      {badge > 0 && badgeStyle === 'count' ? (
        <span className={s.badge} aria-hidden="true">
          {badge > 9 ? '9+' : badge}
        </span>
      ) : null}
    </button>
  );
  return (
    <Tooltip content={label} kind="label" placement={tooltipPlacement} disabled={hideTooltip}>
      {button}
    </Tooltip>
  );
});
